import React from 'react';
import {
  MessageSquare, BarChart2, Calendar, Users, DollarSign,
  FolderOpen, Award, TrendingUp, Target, Layers, Calculator,
  Home, UserCheck, ShieldCheck, Columns3, ChevronLeft, FileText,
} from 'lucide-react';
import { OfficePanelId, OfficeConfig } from '../lib/types';
import { OFFICE_PANEL_LABELS } from '../lib/config';

const ICONS: Record<string, React.ReactNode> = {
  MessageSquare: <MessageSquare size={16} />,
  Columns3: <Columns3 size={16} />,
  BarChart2: <BarChart2 size={16} />,
  Users: <Users size={16} />,
  Calendar: <Calendar size={16} />,
  DollarSign: <DollarSign size={16} />,
  FolderOpen: <FolderOpen size={16} />,
  Award: <Award size={16} />,
  TrendingUp: <TrendingUp size={16} />,
  Target: <Target size={16} />,
  Layers: <Layers size={16} />,
  Calculator: <Calculator size={16} />,
  FileText: <FileText size={16} />,
  Home: <Home size={16} />,
  UserCheck: <UserCheck size={16} />,
  ShieldCheck: <ShieldCheck size={16} />,
};

const PANEL_ICONS: Record<OfficePanelId, string> = {
  chat: 'MessageSquare', kanban: 'Columns3', gantt: 'BarChart2', team: 'Users',
  calendar: 'Calendar', budget: 'DollarSign', projects: 'FolderOpen', sacap: 'Award',
  deals: 'TrendingUp', leads: 'Target', hopper: 'Layers', feasibility: 'Calculator',
  windsor: 'FileText',
  properties: 'Home', tenants: 'UserCheck', compliance: 'ShieldCheck',
};

interface OfficeDockProps {
  office: OfficeConfig;
  activePanel: OfficePanelId;
  onSelect: (panel: OfficePanelId) => void;
  onBack: () => void;
}

/**
 * OfficeDock — macOS-style dock for individual office screens.
 * Floating pill centered at bottom, horizontally scrollable on narrow screens.
 */
export default function OfficeDock({ office, activePanel, onSelect, onBack }: OfficeDockProps) {
  const allItems = office.hqApps;

  return (
    <div className="absolute bottom-3 md:bottom-4 left-1/2 -translate-x-1/2 z-50 select-none max-w-[calc(100vw-16px)]">
      <div
        className="flex items-center gap-0.5 md:gap-1 px-2 md:px-3 py-1.5 md:py-2 rounded-2xl border shadow-sm overflow-x-auto scrollbar-hide"
        style={{ backgroundColor: 'rgba(255,255,255,0.92)', borderColor: `${office.color}40` }}
        data-testid={`office-dock-${office.id}`}
      >
        <button
          onClick={onBack}
          className="dock-icon flex items-center justify-center w-9 h-9 md:w-11 md:h-11 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors shrink-0"
          title="Back to HQ"
          data-testid="office-dock-back"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="h-6 md:h-8 w-px bg-gray-200 mx-0.5 shrink-0" />
        {allItems.map((panelId) => {
          const isActive = panelId === activePanel;
          return (
            <button
              key={panelId}
              onClick={() => onSelect(panelId)}
              className="dock-icon relative flex items-center justify-center w-9 h-9 md:w-11 md:h-11 rounded-xl transition-colors hover:bg-gray-100 shrink-0"
              style={isActive ? { backgroundColor: `${office.color}20` } : {}}
              title={OFFICE_PANEL_LABELS[panelId]}
              data-testid={`office-dock-item-${panelId}`}
            >
              <span style={{ color: isActive ? office.color : '#6b7280' }}>
                {ICONS[PANEL_ICONS[panelId]]}
              </span>
              {isActive && (
                <div className="absolute -bottom-1 w-1 h-1 rounded-full" style={{ backgroundColor: office.color }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
